import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Lock, User, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, devLogin } = useAuth();
  const navigate = useNavigate();
  const isDev = process.env.NODE_ENV === 'development';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }
  };

  const handleDevLogin = () => {
    const result = devLogin();
    if (result.success) {
      navigate('/');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900 px-4">
      {/* Subtle radial background glow */}
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,168,67,0.06)_0%,_transparent_70%)]" />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 text-center">
          <img
            src="/favicon.png"
            alt="Dr. Mali Logo"
            className="mx-auto mb-4 h-24 w-24 rounded-2xl object-contain"
          />
          <h1 className="text-2xl font-bold text-slate-100">Dr. Mali Admin</h1>
          <p className="mt-1 text-sm text-slate-500">
            Financial Wellness AI Dashboard
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-surface-600 bg-surface-800 p-6 shadow-glow"
        >
          <h2 className="mb-5 text-lg font-semibold text-slate-100">
            Sign in to your account
          </h2>

          {error && (
            <div className="mb-4 rounded-xl bg-neon-pink/10 px-4 py-3 text-sm text-neon-pink border border-neon-pink/30">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-slate-300">
              Username
            </label>
            <div className="relative">
              <User
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="w-full rounded-xl border border-surface-600 bg-surface-700 py-2.5 pl-10 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="mb-1.5 block text-sm font-medium text-slate-300">
              Password
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full rounded-xl border border-surface-600 bg-surface-700 py-2.5 pl-10 pr-10 text-sm text-slate-100 placeholder:text-slate-500 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-neon-cyan/20 border border-neon-cyan/40 py-2.5 text-sm font-semibold text-neon-cyan hover:bg-neon-cyan/30 hover:shadow-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent" />
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>

          <p className="mt-4 text-center text-xs text-slate-600">
            Use credentials provided by your administrator
          </p>

          {isDev && (
            <button
              type="button"
              onClick={handleDevLogin}
              className="mt-4 w-full rounded-xl border border-dashed border-slate-600 py-2 text-xs text-slate-500 hover:border-slate-500 hover:text-slate-400 transition-all"
            >
              Dev Login (Skip Auth)
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
